import '../styles/student/App.css';
import '../styles/quiz/Quiz.css';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { fetchUserProfile, saveProfileToLocalStorage } from '../utils/profileService';
import Header from '../components/common/Header';
import Sidebar from '../components/student/Sidebar';
import MainContent from '../components/student/MainContent';
import ModalPortal from '../components/quiz/ModalPortal';
import QuizInterface from '../components/quiz/QuizInterface';
import QuizDashboard from '../components/quiz/QuizDashboard';
import { refreshCache } from '../utils/cacheService';

function StudentDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedNav, setSelectedNav] = useState(null);
  const [showReviewPromptPopup, setShowReviewPromptPopup] = useState(false);

  // Show review prompt popup when landing from "Go to Dashboard" after Final Quiz
  useEffect(() => {
    if (location.state?.showReviewPromptPopup) {
      setShowReviewPromptPopup(true);
      // Clear the state so refresh doesn't re-show
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.showReviewPromptPopup]);

  // Load user profile data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        console.log('🔄 Loading user profile...');
        
        // Check current session
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session?.user?.id) {
          console.error('❌ No user session found:', error);
          return;
        }
        
        console.log('🔐 User session found:', session.user.id);
        
        // Fetch profile from backend
        const profileData = await fetchUserProfile();
        if (profileData) {
          // Save to localStorage so other components can access it
          saveProfileToLocalStorage(profileData);
          console.log('✅ Profile loaded and saved:', profileData);
        } else {
          console.warn('⚠️ No profile data found, using fallback values');
        }
        
      } catch (err) {
        console.error('❌ Error loading user profile:', err);
      }
    };
    
    loadUserProfile();
  }, []);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [showQuizDashboard, setShowQuizDashboard] = useState(false);

  const studentButtons = [
    { id: 1, label: 'Start Learning', icon: '🎓', path: '/reading-session' },
    { id: 2, label: 'Dashboard', icon: '📊' },
    { id: 3, label: 'Logout', icon: '🚪' }
  ];

  const handleNavClick = async (button) => {
    if (button.label === 'Start Learning') {
      // Refresh cache before starting a new reading session
      try {
        await refreshCache();
      } catch (error) {
        console.warn('Cache refresh on start learning failed (continuing):', error);
      }
      return;
    }    
    if (button.label === 'Logout') {
       // Clear backend cache and screenshots (in case they weren't cleared after reading session)
      try {
        await refreshCache();
      } catch (error) {
        console.warn('Cache refresh on logout failed (continuing):', error);
      }
      await supabase.auth.signOut();
      
      // Preserve treats count before clearing localStorage
      const treatsCount = localStorage.getItem('completedReviewsCount');
      
      // Clear all localStorage data
      localStorage.clear();
      
      // Restore treats count after clearing (so it persists across logouts)
      if (treatsCount) {
        localStorage.setItem('completedReviewsCount', treatsCount);
      }
      
      // Clear sessionStorage (which includes viewed questions state and session flag)
      sessionStorage.clear();
      
      // Navigate to home page
      navigate('/');
    } else {
      setSelectedNav(button);
    }
  };

  const handleFinalQuizComplete = () => {
    setShowQuiz(false);
    setShowQuizDashboard(true);
  };

  const handleQuizDashboardComplete = () => {
    setShowQuizDashboard(false);
    setCurrentQuizIndex(0);
  };

  // Show Quiz Dashboard
  if (showQuizDashboard) {
    return (
      <div className="App app-bg">
        <QuizDashboard onStartNewQuiz={handleQuizDashboardComplete} />
      </div>
    );
  }

  return (
    <div className="student-app student-app-bg">
      <Header buttons={studentButtons} onNavClick={handleNavClick} activeButton={2} />
      <div className="student-app-body">
        <div className="student-left-section">
          <Sidebar />
        </div>
        <div className="student-right-section">
          <MainContent selectedNav={selectedNav} />
        </div>
      </div>

      {/* Review prompt popup: shown when landing from Go to Dashboard after Final Quiz */}
      {showReviewPromptPopup && (
        <div className="survey-popup-overlay" onClick={() => setShowReviewPromptPopup(false)}>
          <div className="survey-popup" onClick={(e) => e.stopPropagation()}>
            <button className="survey-popup-close" onClick={() => setShowReviewPromptPopup(false)} aria-label="Close">&times;</button>
            <p className="survey-popup-text">
              Review ALL of your quiz answers to collect a treat!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentDashboard;
